import type {
    GalleryPhoto,
    MemoryClip,
    SectionRow,
    SettingsRow
} from '@/types'

import { Clapperboard, Video } from 'lucide-react'

import ClipsPlayer from '@/components/landing/clips-player'
import MemoriesReel from '@/components/landing/memories-reel'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface VideoSectionProps {
    section?: SectionRow
    clipsSection?: SectionRow
    settings: SettingsRow | null
    photos?: Array<GalleryPhoto>
    clips?: Array<MemoryClip>
}

/**
 * Plays the memories video uploaded in the CMS. Until one is uploaded, the
 * gallery photos play as a short film instead. The video clips play below it,
 * and the personal video message appears only once it has been uploaded.
 */
export default function VideoSection({ section, clipsSection, settings, photos = [], clips = [] }: VideoSectionProps) {
    const memoriesVideo = settings?.memories_video
    const messageVideo = settings?.message_video
    const poster = photos.find(photo => photo.photo && photo.photo_rotation === 0)?.photo ?? undefined

    if (!memoriesVideo && !messageVideo && photos.length === 0 && clips.length === 0) return null

    return (
        <section id='video' className='relative overflow-hidden bg-night py-24 text-cream sm:py-32'>
            <div aria-hidden className='absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,#4a1336_0%,#1a0916_65%)]' />
            <div aria-hidden className='star-dust absolute inset-0 opacity-60' />

            <div className='relative mx-auto max-w-5xl px-6 lg:px-10'>
                <SectionHeading section={section} tone='dark' />

                {(memoriesVideo || photos.length > 0) && (
                    <Reveal className='mt-14 sm:mt-16' direction='zoom'>
                        {memoriesVideo
                            ? (
                                <div className='overflow-hidden rounded-[2rem] bg-black shadow-[0_40px_90px_-40px_rgba(232,84,124,0.55)] ring-1 ring-white/10'>
                                    <video
                                        src={memoriesVideo}
                                        poster={poster}
                                        controls
                                        playsInline
                                        preload='metadata'
                                        className='aspect-video w-full bg-black'
                                    />
                                </div>
                            )
                            : <MemoriesReel photos={photos} />}
                    </Reveal>
                )}

                {clips.length > 0 && (
                    <div id='clips' className='mt-20 scroll-mt-8 sm:mt-24'>
                        <SectionHeading section={clipsSection} tone='dark' level='h3' size='md' />

                        <Reveal className='mt-10' direction='zoom'>
                            <ClipsPlayer clips={clips} />
                        </Reveal>
                    </div>
                )}

                {messageVideo && (
                    <Reveal className='mx-auto mt-14 max-w-3xl' delay={100}>
                        <div className='rounded-[2rem] bg-white/[0.06] p-4 ring-1 ring-white/10 backdrop-blur-sm sm:p-6'>
                            <div className='flex items-center gap-3 px-1 pb-4'>
                                <span className='grid size-10 shrink-0 place-items-center rounded-full bg-petal/20 text-[#ffb3c6]'>
                                    <Video aria-hidden className='size-5' />
                                </span>

                                {settings?.message_video_title
                                    ? (
                                        <h3
                                            data-mdlfy-cms={`settings:${settings.id}:message_video_title`}
                                            className='font-heading text-2xl text-cream italic'
                                        >
                                            {settings.message_video_title}
                                        </h3>
                                    )
                                    : <Clapperboard aria-hidden className='size-5 text-cream/60' />}
                            </div>

                            <video
                                src={messageVideo}
                                controls
                                playsInline
                                preload='metadata'
                                className='aspect-video w-full rounded-2xl bg-black'
                            />
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    )
}
